/* eslint-disable max-len */
/* eslint-disable no-console */
import { API } from 'aws-amplify';
import { toast } from 'react-toastify';
import { catchErr } from '../../../libs/HelperContent';

// API endpoint name - should match configuration in AWS Amplify
const API_NAME = 'resourceManagementADI';

/**
 * TypeScript interfaces for document management
 */
export interface DocumentMetadata {
	author?: string;
	createdDate?: string;
	modifiedDate?: string;
	pageCount?: number;
	fileType?: string;
}

export interface DocumentViewerSettings {
	enableDownload?: boolean;
	enableShare?: boolean;
	enableCopy?: boolean;
	showMetadata?: boolean;
}

export interface DocumentFile {
	title: string;
	description?: string;
	url: string;
	fileType: string;
	fileSize?: number;
	order: number;
	uploadedAt: string;
	categoryIds: string[];
	tags: string[];
	status: 'draft' | 'published' | 'scheduled' | 'unpublished';
	visibility: 'private' | 'public';
	allowDownload: boolean;
	allowShare: boolean;
	accessControl: {
		internalEmployees?: {
			allSelected?: boolean;
			departments?: Array<{ name: string; count?: number }>;
			specificUsers?: Array<{ userId: string; name: string; email: string; role?: string }>;
		};
		userGroups?: Array<{ type: string; count?: number }>;
		participantCount?: number;
		participantLimit?: number;
	};
	pageCount?: number;
	metadata?: DocumentMetadata;
}

export interface CreateDocumentPayload {
	companyId: string;
	sourceType: 'system' | 'user' | 'external' | 'media_drive' | 'embed_link';
	files: DocumentFile[];
	publishAt?: string;
	sourceId?: string;
	viewerSettings?: DocumentViewerSettings;
}

export interface DocumentCreatedBy {
	userId: string;
	name: string;
	role?: string;
}

export interface DocumentEngagement {
	views: number;
	downloads: number;
	helpful: number;
	notHelpful: number;
}

export interface DocumentResponse {
	_id: string;
	companyId: string;
	sourceType: 'system' | 'user' | 'external' | 'media_drive';
	files: DocumentFile[];
	createdBy: DocumentCreatedBy;
	updatedBy: DocumentCreatedBy;
	engagement: DocumentEngagement;
	createdAt: string;
	updatedAt: string;
	publishedAt?: string;
	scheduledPublishDate?: string;
	viewerSettings?: DocumentViewerSettings;
	__v: number;
}

export interface DocumentListResponse {
	data: DocumentResponse[];
	pagination: {
		total: number;
		page: number;
		limit: number;
	};
}

export interface UpdateDocumentPayload {
	sourceType?: 'system' | 'user' | 'external' | 'media_drive';
	files?: Array<{
		title?: string;
		description?: string;
		url?: string;
		fileType?: string;
		fileSize?: number;
		order?: number;
		uploadedAt?: string;
		categoryIds?: string[];
		tags?: string[];
		status?: 'draft' | 'published' | 'scheduled' | 'unpublished';
		visibility?: 'private' | 'public';
		allowDownload?: boolean;
		allowShare?: boolean;
		accessControl?: {
			internalEmployees?: {
				allSelected?: boolean;
				departments?: Array<{ name: string; count?: number }>;
				specificUsers?: Array<{ userId: string; name: string; email: string; role?: string }>;
			};
			userGroups?: Array<{ type: string; count?: number }>;
			participantCount?: number;
			participantLimit?: number;
		};
		pageCount?: number;
		metadata?: DocumentMetadata;
	}>;
	viewerSettings?: DocumentViewerSettings;
}

/**
 * Create a new document collection
 * @param documentPayload - Document collection payload
 * @returns Promise with API response
 */
export const createDocumentCollection = async (documentPayload: CreateDocumentPayload) =>
{
	try
	{
		const apiResponse = await API.post(API_NAME, '/documents', {
			body : documentPayload
		});

		return apiResponse;
	}
	catch (error)
	{
		console.error('Error creating document collection:', error);
		throw error;
	}
};

/**
 * Get all document collections for a company
 * @param companyId - Company ID
 * @param options - Additional options like pagination, filters
 * @returns Promise with API response
 */
export const getDocumentCollections = async (
	companyId: string,
	options: {
		page?: number;
		limit?: number;
		status?: string;
		category?: string;
		search?: string;
		count?: boolean;
	} = {},
	from = 'Configuration'
) =>
{
	try
	{
		const { page, limit, status, category, search } = options;

		let queryParams = '';

		// let queryParams = `?page=${page}&limit=${limit}`;
		const path = `/documents/company/${companyId}`;

		if (page) queryParams += `&page=${page}`;
		if (limit) queryParams += `&limit=${limit}`;
		if (status) queryParams += `&status=${status}`;
		if (category) queryParams += `&category=${category}`;
		if (search) queryParams += `&search=${encodeURIComponent(search)}`;
		if (from) queryParams += `&from=${from}`;
		const apiResponse = await API.get(API_NAME, `${path}?${queryParams}`, {});

		return apiResponse;
	}
	catch (error)
	{
		console.error('Error fetching document collections:', error);
		throw error;
	}
};

/**
 * Get a single document collection by ID
 * @param collectionId - Document collection ID
 * @returns Promise with API response
 */
export const getDocumentCollectionById = async (collectionId: string, queryStringParameters) =>
{
	try
	{
		const apiResponse = await API.get(API_NAME, `/documents/${collectionId}`, { queryStringParameters });

		return apiResponse;
	}
	catch (error)
	{
		console.error('Error fetching document collection:', error);
		throw error;
	}
};

/**
 * Update a document collection
 * @param collectionId - Document collection ID
 * @param updatePayload - Update payload
 * @returns Promise with API response
 */
export const updateDocumentCollection = async (
	collectionId: string,
	updatePayload: UpdateDocumentPayload
) =>
{
	console.log(updatePayload);
	try
	{
		const apiResponse = await API.put(API_NAME, `/documents/${collectionId}`, {
			body : updatePayload
		});

		// toast.success('Document updated successfully');

		return apiResponse;
	}
	catch (error)
	{
		console.error('Error updating document collection:', error);
		throw error;
	}
};

/**
 * Delete a document collection
 * @param collectionId - Document collection ID
 * @returns Promise with API response
 */
export const deleteDocumentCollection = async (collectionId: any) =>
{
	try
	{
		const apiResponse = await API.del(API_NAME, `/documents/${collectionId}`, {});

		return apiResponse;
	}
	catch (error)
	{
		console.error('Error deleting document collection:', error);
		throw error;
	}
};

/**
 * Get all documents for a company with pagination and filters
 * @param companyId - Company ID
 * @param params - Query parameters
 * @returns Promise with documents list and pagination
 */
export const getDocumentsList = async (
	companyId: string,
	params?: {
		page?: number;
		limit?: number;
		status?: string;
		category?: string;
		subCategory?: string;
		tag?: string;
		sourceType?: string;
		sort?: string;
	}
): Promise<DocumentListResponse> =>
{
	try
	{
		// Default values
		const {
			page = 1,
			limit = 20,
			status,
			category,
			subCategory,
			tag,
			sourceType,
			sort = '-createdAt'
		} = params || {};

		// Build query string
		let queryString = `?companyId=${companyId}&page=${page}&limit=${limit}&sort=${sort}`;

		if (status) queryString += `&status=${status}`;
		if (category) queryString += `&category=${category}`;
		if (subCategory) queryString += `&subCategory=${subCategory}`;
		if (tag) queryString += `&tag=${tag}`;
		if (sourceType) queryString += `&sourceType=${sourceType}`;

		const response = await API.get(API_NAME, `/documents${queryString}`, {});

		return response;
	}
	catch (error)
	{
		console.error('Error fetching documents:', error);
		throw error;
	}
};

/**
 * Track document engagement (view, download, etc.)
 * @param documentId - Document ID
 * @param fileIndex - Index of the file in the collection
 * @param engagementType - Type of engagement
 * @returns Promise with success indicator
 */
export const trackDocumentEngagement = async (
	documentId: string,
	fileIndex: number,
	engagementType: 'view' | 'download' | 'share' | 'like'
): Promise<{ success: boolean }> =>
{
	try
	{
		const response = await API.post(API_NAME, `/documents/${documentId}/engagement`, {
			body : {
				fileIndex,
				type : engagementType
			}
		});

		return response;
	}
	catch (error)
	{
		console.error(`Error tracking document ${engagementType}:`, error);

		return { success: false };
	}
};

/**
 * Internal helper to update a document without showing toasts
 * (Used by handleMultipleDocumentUpdates to avoid duplicate toasts)
 */
export const updateDocumentSilent = async (
	documentId: string,
	options: UpdateDocumentPayload
): Promise<DocumentResponse> =>
{
	try
	{
		const response = await API.put(API_NAME, `/documents/${documentId}`, {
			body : options
		});

		return response.data;
	}
	catch (error)
	{
		console.error('Error updating document:', error);
		throw error;
	}
};

/**
 * Handles updating multiple documents at once with the same data
 * 
 * @param companyId - The company ID
 * @param documentIds - Array of document IDs to update
 * @param updatedData - The data to apply to all documents
 * @returns Promise with array of responses
 */
export const handleMultipleDocumentUpdates = async (
	companyId: string,
	documentIds: string[],
	updatedData: Partial<UpdateDocumentPayload>
): Promise<DocumentResponse[]> =>
{
	if (!documentIds || documentIds.length === 0)
	{
		throw new Error('No document IDs provided for update');
	}

	try
	{
		// Show an initial toast for starting the update
		const isBatchUpdate = documentIds.length > 1;
		// const toastId = toast.info(
		// 	isBatchUpdate
		// 		? `Updating ${documentIds.length} documents...`
		// 		: 'Updating document...',
		// 	{ autoClose: false }
		// );

		// Process each document update as a separate operation
		const updatePromises = documentIds.map((documentId) =>
		{
			// Use the existing update function but suppress its toasts
			return updateDocumentSilent(documentId, updatedData);
		});

		// Wait for all updates to complete
		const responses = await Promise.all(updatePromises);

		toast.success(isBatchUpdate
			? `${documentIds.length} documents updated successfully!`
			: 'Document updated successfully!');

		// Show success message
		// toast.update(toastId, {
		// 	render : isBatchUpdate
		// 		? `${documentIds.length} documents updated successfully!`
		// 		: 'Document updated successfully!',
		// 	type      : toast.TYPE.SUCCESS,
		// 	autoClose : 3000
		// });

		return responses;
	}
	catch (error)
	{
		console.error('Error updating documents:', error);

		// Provide error message
		const errorMessage = documentIds.length > 1
			? `Failed to update some or all of the ${documentIds.length} documents`
			: 'Failed to update document';

		toast.error(errorMessage);
		throw error;
	}
};

/**
 * Update document status using the status-specific endpoint
 * @param documentId - Document ID
 * @param status - New status ('draft' | 'published' | 'scheduled' | 'unpublished')
 * @param fileIndex - Index of the file in the collection (default: 0)
 * @param scheduledPublishDate - Required if status is 'scheduled'
 * @returns Promise with API response
 */
export const updateDocumentStatus = async (
	documentId: string,
	status: 'draft' | 'published' | 'scheduled' | 'unpublished',
	scheduledPublishDate?: number,
	fileIndex: number = 0
): Promise<{ success: boolean; data: DocumentResponse }> =>
{
	try
	{
		const payload = {
			fileIndex,
			status,
			...(status === 'scheduled' && scheduledPublishDate ? { scheduledPublishDate } : {})
		};

		const response = await API.patch(API_NAME, `/documents/${documentId}/status`, {
			body : payload
		});

		return response;
	}
	catch (error)
	{
		console.error('Error updating document status:', error);
		catchErr(error);
		throw error;
	}
};

/**
 * Delete a document by ID
 * Implements the DELETE /api/v1/documents/:id endpoint
 * @param documentId - Document ID to delete
 * @returns Promise with deletion response { success: true, message: string }
 */
export const deleteDocument = async (documentId: string): Promise<{ success: boolean; message?: string }> =>
{
	try
	{
		const response = await API.del(API_NAME, `/documents/${documentId}`, {});

		toast.success('Document deleted successfully');

		return response;
	}
	catch (error: any)
	{
		console.error('Error deleting document:', error);
		throw error;
	}
};

/**
 * Toggle favorite status for a document
 * @param companyId - Company ID
 * @param documentId - Document ID
 * @returns Promise with favorite status and updated document
 */
export const toggleDocumentFavorite = async (
	companyId: string,
	documentId: string
): Promise<{
	success: boolean;
	data: {
		document: {
			_id: string;
			sourceType: string;
			companyId: string;
			files: Array<{
				title: string;
				description: string;
				url: string;
				fileType: string;
				status: string;
				categoryIds: string[];
				tags: string[];
			}>;
			favorites: Array<{
				userId: string;
				name: string;
				favoritedAt: string;
			}>;
			favoriteCount: number;
		};
		isFavorited: boolean;
		favoriteCount: number;
	};
	message: string;
}> =>
{
	try
	{
		const response = await API.post(API_NAME, `/documents/company/${companyId}/${documentId}/favorite`, {});

		return response;
	}
	catch (error)
	{
		console.error('Error toggling document favorite:', error);
		throw error;
	}
};
export const trackDocumentDownload = async (documentId: string) =>
{
	const response = await API.post(API_NAME, `/documents/${documentId}/download`, {});

	return response;
};

export const trackDocumentView = async (documentId: string) =>
{
	const response = await API.post(API_NAME, `/documents/${documentId}/view`, {});

	return response;
};

export const updateDocumentEngagement = async (documentId: string, metric: string, value: number = 1) =>
{
	const response = await API.patch(API_NAME, `/documents/${documentId}/engagement`, {
		body : {
			metric,
			value
		}
	});
};

/**
 * Track page view in a document
 * @param documentId - Document ID
 * @param body - Body of the request
 * @returns Promise with API response
 */
export const trackDocumentPageView = async (
	documentId: string,
	body: {
		pageNumber: number;
		timeSpent?: number;
	}
): Promise<{ success: boolean; data?: any }> =>
{
	try
	{
		const response = await API.post(
			API_NAME,
			`/documents/${documentId}/files/0/page-view`,
			{
				body
			}
		);

		return {
			success : true,
			data    : response
		};
	}
	catch (error)
	{
		console.error('Error tracking document page view:', error);

		return {
			success : false
		};
	}
};
